import React, { useEffect, useRef } from 'react';

import Message from './Message';
import gql from 'graphql-tag';
import Loader from 'react-loading';
import { startCase } from 'lodash';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { themes, MessageType, ConversationType } from 'kb-shared';

import MessageInput from './MessageInput';
import ProviderIntro from './ProviderIntro';
import { ConversationResponse, CONVERSATION_QUERY } from './graphql/conversationQuery';
import { reportError } from 'utilities/errors';
import { Centered, Main, Messages, Subject } from './styled';

interface Props {
  conversationId: number;
}

const markConversationMessagesRead = gql`
  mutation markConversationMessagesRead($id: Int!) {
    markConversationMessagesRead(id: $id) {
      conversation {
        id
        unreadCount
        lastMessageTimestamp
      }
    }
  }
`;

const Conversation = ({ conversationId }: Props) => {
  const [markAsRead] = useMutation<ConversationType>(markConversationMessagesRead);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const { data, error, refetch } = useQuery<ConversationResponse>(CONVERSATION_QUERY, {
    variables: {
      id: typeof conversationId === 'number' ? conversationId : parseInt(conversationId)
    },
    fetchPolicy: 'cache-and-network'
  });

  if (error) {
    reportError(error, { name: 'ConversationQueryError' });
  }

  const scrollMessagesToBottom = () => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  };

  const afterSendCallback = async () => {
    await refetch();
  };

  useEffect(() => {
    scrollMessagesToBottom();
  }, [data]);

  useEffect(() => {
    if (data?.conversation && data.conversation.unreadCount > 0) {
      // ensure marked as read even if active convo isn't changed
      markAsRead({
        variables: {
          id: parseInt(data.conversation.id)
        }
      });
    }
  }, [data, markAsRead]);

  if (!data) {
    return (
      <Main>
        <Centered>
          <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
        </Centered>
      </Main>
    );
  }

  const { conversation } = data;
  const { subject, category, assignedToUserName, assignedToUserRole, messages } = conversation;

  return (
    <Main>
      <Subject>
        {assignedToUserName || `Kindbody ${startCase(category)} Team`} &bull; {subject}
      </Subject>
      <Messages ref={messagesRef}>
        <ul>
          {assignedToUserName && assignedToUserRole && (
            <ProviderIntro name={assignedToUserName} role={assignedToUserRole} />
          )}
          {messages.map((message: MessageType) => (
            <Message key={message.id} message={message} />
          ))}
        </ul>
      </Messages>
      <MessageInput
        conversationId={conversation.id}
        afterSend={afterSendCallback}
        status={conversation.status}
      />
    </Main>
  );
};

export default Conversation;
